import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import pluralize from 'pluralize';
import {
    TextInput,
    ThemedButton,
    BigModal,
    FlexDiv,
    SearchInput,
} from '@awarego-ui/components';
import {
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    Typography,
} from '@mui/material';
import { ThemedLink } from '../../../components/themed/ThemedComponents';
import { useStores } from '../../../hooks/use-stores';
import ThemedSpinner from '../../../components/themed/ThemedSpinner';
import services from '../../../services';

const DynamicListCreateModal = ({
    companyId,
    isModalOpen,
    onRequestClose,
    onSuccess,
    errorSetter,
}) => {
    const { listStore, dynamicListCreateStore } = useStores();
    const [listName, setListName] = useState('');
    const [includeType, setIncludeType] = useState('both');
    const [loading, setLoading] = useState(false);

    const store = dynamicListCreateStore;

    // Load lists on mount
    useEffect(() => {
        if (isModalOpen && companyId) {
            listStore.loadCompanyLists(companyId);
        }
    }, [isModalOpen, companyId, listStore]);

    // Reset form on open and cleanup on unmount
    useEffect(() => {
        if (isModalOpen) {
            setListName('');
            setIncludeType('both');
            store.reset();
        }

        return () => {
            if (!isModalOpen) {
                // Cleanup reaction if needed
            }
        };
    }, [isModalOpen, store]);

    const toggleRecipients = async (id, e) => {
        e.stopPropagation();
        store.toggleRecipients(id);

        if (store.recipientsShown.includes(id)) {
            await store.loadUsersForList(companyId, id);
        }
    };

    const handleToggleRow = (list, checked) => {
        store.selection.toggleRow(list, checked);
    };

    const handleTogglePage = (checked) => {
        store.selection.toggleCurrentPageSelected(checked);
    };

    const handleSubmit = async () => {
        if (!listName.trim()) {
            errorSetter('List name is required');
            return;
        }

        setLoading(true);
        try {
            const selectedListIds = store.getSelectedListIds();
            await services.Companies.listsService(companyId).createDynamic({
                name: listName.trim(),
                sourceListIds: selectedListIds,
                includeType: includeType,
            });
            onSuccess();
        } catch (e) {
            errorSetter('Error creating dynamic list: ' + (e.message || 'Unknown error'));
            console.error('Error creating dynamic list:', e);
        } finally {
            setLoading(false);
        }
    };

    const currentPageSelected = store.selection.currentPageSelected;
    const effectiveSelection = store.selection.effectiveSelectionOnCurrentPage;

    return (
        <BigModal
            isOpen={isModalOpen}
            onRequestClose={onRequestClose}
            heading="Create new Awareness Improvements list"
            modalContent={
                <>
                    <TextInput
                        stretched
                        value={listName}
                        onChange={(e) => setListName(e.target.value)}
                        placeholder="Enter list name"
                        style={{ marginBottom: 24 }}
                    />
                    {/*<SearchInput*/}
                    {/*    searchValue={store.searchValue}*/}
                    {/*    placeholder="Search lists"*/}
                    {/*    onChange={(e) => {*/}
                    {/*        store.setSearchValue(e.target.value || '');*/}
                    {/*    }}*/}
                    {/*    onClear={() => {*/}
                    {/*        store.setSearchValue('');*/}
                    {/*    }}*/}
                    {/*    style={{ marginBottom: 24 }}*/}
                    {/*/>*/}

                    {store.selectedListsCount > 0 && (
                        <Typography variant="body2" style={{ marginBottom: 16 }}>
                            {store.selectedListsCount}{' '}
                            {pluralize('list', store.selectedListsCount)} selected
                            {store.selectedEmployeesCount > 0 && (
                                <> with {store.selectedEmployeesCount} {pluralize('employee', store.selectedEmployeesCount)}</>
                            )}
                        </Typography>
                    )}

                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={currentPageSelected}
                                            indeterminate={
                                                store.selection.haveDeselectedOnCurrentPage
                                            }
                                            onChange={(e) =>
                                                handleTogglePage(e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>Lists ({store.availableLists.length})</TableCell>
                                    <TableCell># Employees</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {store.paginatedLists.map((list) => (
                                    <React.Fragment key={list.id}>
                                        <TableRow
                                            onClick={() => {
                                                handleToggleRow(
                                                    list,
                                                    !effectiveSelection[list.id]
                                                );
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={
                                                        effectiveSelection[list.id] || false
                                                    }
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleRow(list, e.target.checked);
                                                    }}
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{list.name}</TableCell>
                                            <TableCell>{list.usersCount || 0}</TableCell>
                                            {list.usersCount > 0 && (
                                                <TableCell>
                                                    <ThemedLink
                                                        onClick={(e) =>
                                                            toggleRecipients(list.id, e)
                                                        }
                                                    >
                                                        {store.recipientsShown.includes(list.id)
                                                            ? 'Hide'
                                                            : 'Show'}{' '}
                                                        recipients
                                                    </ThemedLink>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        {store.recipientsShown.includes(list.id) && (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Collapse
                                                        in={store.recipientsShown.includes(list.id)}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <div style={{ padding: 16 }}>
                                                            {store.loadingUsers.includes(list.id) ? (
                                                                <CircularProgress size={25} />
                                                            ) : (
                                                                list.users &&
                                                                list.users.map &&
                                                                list.users.map((u) => (
                                                                    <div key={u.email || u.id}>
                                                                        {u.email}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>

                        {store.totalPages > 1 && (
                            <FlexDiv gap={8} mt={16} alignCenter justifyCenter>
                                <ThemedButton
                                    mode="secondary"
                                    text="Previous"
                                    onClick={() => store.setPagingParams(Math.max(0, store.pageIndex - 1), store.pageSize)}
                                    disabled={store.pageIndex === 0}
                                />
                                <Typography variant="body2">
                                    Page {store.pageIndex + 1} of {store.totalPages}
                                </Typography>
                                <ThemedButton
                                    mode="secondary"
                                    text="Next"
                                    onClick={() =>
                                        store.setPagingParams(Math.min(store.totalPages - 1, store.pageIndex + 1), store.pageSize)
                                    }
                                    disabled={store.pageIndex >= store.totalPages - 1}
                                />
                            </FlexDiv>
                        )}
                    </div>

                    <FormControl component="fieldset">
                        <FormLabel component="legend">Whom to include</FormLabel>
                        <RadioGroup
                            row
                            value={includeType}
                            onChange={(e) => setIncludeType(e.target.value)}
                        >
                            <FormControlLabel
                                value="attendance"
                                control={<Radio />}
                                label="Low attendance"
                            />
                            <FormControlLabel
                                value="performance"
                                control={<Radio />}
                                label="Low performers"
                            />
                            <FormControlLabel
                                value="both"
                                control={<Radio />}
                                label="Both"
                            />
                        </RadioGroup>
                    </FormControl>
                </>
            }
            modalFooterContent={
                loading ? (
                    <ThemedSpinner size={50} />
                ) : (
                    <>
                        <ThemedButton
                            mode="secondary"
                            onClick={onRequestClose}
                            text="Cancel"
                        />
                        <ThemedButton
                            mode="primary"
                            onClick={handleSubmit}
                            disabled={!listName.trim()}
                            text="Create"
                        />
                    </>
                )
            }
        />
    );
};

export default observer(DynamicListCreateModal);

